"use client";

import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, PhoneCall, ShieldCheck, Search, FileText, Wrench } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const processSteps = [{
  number: "01",
  title: "Consulta y Diagnóstico",
  description: "Inspeccionamos minuciosamente la avería o proyecto con tecnología de precisión sin realizar obras innecesarias.",
  icon: Search
}, {
  number: "02",
  title: "Presupuesto Transparente",
  description: "Elaboramos una propuesta clara, valorada y detallada antes de iniciar, garantizando cero costes ocultos.",
  icon: FileText
}, {
  number: "03",
  title: "Ejecución Profesional",
  description: "Técnicos certificados ejecutan el servicio con la máxima eficacia, herramientas modernas y total orden.",
  icon: Wrench
}, {
  number: "04",
  title: "Garantía y Entrega",
  description: "Comprobamos el correcto funcionamiento de la instalación y entregamos la garantía por escrito.",
  icon: ShieldCheck
}];
export default function Workflow() {
  return <section id="workflowprocess" className="bg-background py-20 md:py-28 lg:py-36 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="flex items-center justify-center gap-2 mb-4">
            <Check className="size-4 text-[var(--chart-1)]" />
            <Badge variant="outline" className="border-[var(--chart-1)]/30 text-foreground font-mono uppercase tracking-wider text-xs px-3 py-1 bg-background">
              Proceso Transparente
            </Badge>
            <Check className="size-4 text-[var(--chart-1)]" />
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="font-default text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground">
            Cómo Trabajamos
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Método estructurado y directo para resolver sus problemas de fontanería, detección de fugas y mantenimiento en Totana y la región de Murcia.
          </motion.p>
        </div>

        {}
        <div className="relative">
          
          {}
          <div className="hidden lg:block absolute top-[2.75rem] left-[10%] right-[10%] h-[2px] bg-[var(--chart-1)] z-0" aria-hidden="true" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 relative z-10">
            {processSteps.map((step, i) => {
            const StepIcon = step.icon;
            return <motion.div key={step.number} data-index={i} initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.15
            }} className="flex flex-col h-full">
                  <Card className="bg-card border border-border shadow-xs hover:shadow-md transition-shadow duration-300 flex-1 rounded-xl p-6 relative flex flex-col justify-between">
                    <div>
                      {}
                      <div className="flex items-center justify-between mb-6">
                        <span className="font-mono text-3xl font-bold text-foreground tracking-tight bg-background px-3 py-1 rounded-lg border border-border">
                          {step.number}
                        </span>
                        <div className="size-11 rounded-lg bg-[var(--chart-1)]/10 text-[var(--chart-1)] flex items-center justify-center">
                          <StepIcon className="size-5" />
                        </div>
                      </div>

                      {}
                      <h3 className="font-default text-xl font-bold text-foreground mb-3 tracking-tight">
                        {step.title}
                      </h3>
                      
                      <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                        {step.description}
                      </p>
                    </div>

                    <div className="mt-6 pt-4 border-t border-border/50 flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[var(--chart-1)] font-medium">
                      <span>Paso {step.number} de 04</span>
                    </div>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>

        {}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="mt-16 md:mt-20 p-8 sm:p-10 rounded-xl bg-card border border-border shadow-sm flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h4 className="font-default text-xl font-bold text-foreground">
              ¿Tiene una urgencia o consulta sobre su instalación?
            </h4>
            <p className="text-sm text-muted-foreground font-sans">
              Estamos disponibles para ofrecerle asesoramiento profesional y soluciones inmediatas.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto bg-[var(--chart-1)] text-primary-foreground hover:bg-[var(--chart-1)]/90 font-medium">
              <Link to="/ContactPage">
                <PhoneCall className="size-4" />
                Contactar Ahora
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/ServicesPage">
                Ver Servicios
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </motion.div>

      </div>
    </section>;
}